// =============================================================================
// Origin UI — src/pages/admin/OnboardingChecklistAdminTab.tsx
// Admin Config tab for managing onboarding document checklists.
// WS-4 Batch 4.3 — Onboarding Lifecycle module.
// =============================================================================

import { useState, useEffect } from 'react';
import { api } from '@/services/apiService';
import { Card, ErrorBanner, LoadingSpinner, ConfirmDialog } from '@/components/common';
import styles from './admin.module.css';

// ─── Types ───

interface ChecklistListItem {
  checklistId: string; officeLocationId: number; officeName: string;
  employmentType: string; name: string; isActive: boolean; itemCount: number;
}

interface ChecklistDetail {
  checklistId: string; officeLocationId: number; officeName: string;
  employmentType: string; name: string; isActive: boolean;
  createdBy: string; createdAt: string; updatedBy: string; updatedAt: string;
  items: ChecklistItem[];
}

interface ChecklistItem {
  checklistItemId: string; checklistId: string; documentName: string;
  documentCategory: string; isRequired: boolean; hasExpiry: boolean;
  sortOrder: number; notes: string | null;
}

interface OfficeLocation { officeLocationId: number; name: string; }

const EMPLOYMENT_TYPES = ['Permanent', 'FixedTerm', 'Contractor'];
const DOC_CATEGORIES = ['ID', 'Visa', 'Medical', 'Certificate', 'Contract', 'Other'];

// ─── Component ───

export function OnboardingChecklistAdminTab() {
  const [checklists, setChecklists] = useState<ChecklistListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [offices, setOffices] = useState<OfficeLocation[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ChecklistDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ officeLocationId: 0, employmentType: '', name: '' });
  const [creating, setCreating] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [itemForm, setItemForm] = useState({ documentName: '', documentCategory: 'Other', isRequired: true, hasExpiry: false, sortOrder: 0, notes: '' });
  const [savingItem, setSavingItem] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ checklistId: string; itemId: string; name: string } | null>(null);
  const [editingChecklist, setEditingChecklist] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', isActive: true });
  const [savingChecklist, setSavingChecklist] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      setLoading(true); setError('');
      const [cls, ofcs] = await Promise.all([
        api.get<ChecklistListItem[]>('/api/admin/checklists'),
        api.get<OfficeLocation[]>('/api/reference/office-locations'),
      ]);
      setChecklists(cls); setOffices(ofcs);
    } catch (err: any) { setError(err.message || 'Failed to load'); }
    finally { setLoading(false); }
  }

  async function loadDetail(id: string) {
    try {
      setDetailLoading(true);
      const d = await api.get<ChecklistDetail>(`/api/admin/checklists/${id}`);
      setDetail(d); setEditForm({ name: d.name, isActive: d.isActive });
    } catch (err: any) { setError(err.message || 'Failed to load checklist'); }
    finally { setDetailLoading(false); }
  }

  async function handleExpand(id: string) {
    if (expandedId === id) { setExpandedId(null); setDetail(null); return; }
    setExpandedId(id); await loadDetail(id);
  }

  async function handleCreate() {
    if (!createForm.officeLocationId || !createForm.employmentType || !createForm.name.trim()) { setError('All fields are required'); return; }
    try {
      setCreating(true); setError('');
      await api.post('/api/admin/checklists', createForm);
      setShowCreate(false); setCreateForm({ officeLocationId: 0, employmentType: '', name: '' }); await load();
    } catch (err: any) { setError(err.message || 'Failed to create'); }
    finally { setCreating(false); }
  }

  async function handleUpdateChecklist() {
    if (!detail) return;
    try {
      setSavingChecklist(true);
      await api.put(`/api/admin/checklists/${detail.checklistId}`, editForm);
      setEditingChecklist(false); await loadDetail(detail.checklistId); await load();
    } catch (err: any) { setError(err.message || 'Failed to update'); }
    finally { setSavingChecklist(false); }
  }

  function startAddItem() {
    const nextSort = detail ? Math.max(0, ...detail.items.map(i => i.sortOrder)) + 1 : 0;
    setItemForm({ documentName: '', documentCategory: 'Other', isRequired: true, hasExpiry: false, sortOrder: nextSort, notes: '' });
    setEditingItemId(null); setShowItemForm(true);
  }

  function startEditItem(item: ChecklistItem) {
    setItemForm({ documentName: item.documentName, documentCategory: item.documentCategory, isRequired: item.isRequired, hasExpiry: item.hasExpiry, sortOrder: item.sortOrder, notes: item.notes || '' });
    setEditingItemId(item.checklistItemId); setShowItemForm(true);
  }

  async function handleSaveItem() {
    if (!detail || !itemForm.documentName.trim()) { setError('Document name is required'); return; }
    try {
      setSavingItem(true); setError('');
      if (editingItemId) { await api.put(`/api/admin/checklists/${detail.checklistId}/items/${editingItemId}`, itemForm); }
      else { await api.post(`/api/admin/checklists/${detail.checklistId}/items`, itemForm); }
      setShowItemForm(false); setEditingItemId(null); await loadDetail(detail.checklistId); await load();
    } catch (err: any) { setError(err.message || 'Failed to save item'); }
    finally { setSavingItem(false); }
  }

  async function handleDeleteItem() {
    if (!deleteTarget) return;
    try {
      await api.delete(`/api/admin/checklists/${deleteTarget.checklistId}/items/${deleteTarget.itemId}`);
      setDeleteTarget(null); if (detail) await loadDetail(detail.checklistId); await load();
    } catch (err: any) { setError(err.message || 'Failed to delete'); setDeleteTarget(null); }
  }

  if (loading) return <LoadingSpinner message="Loading checklists..." />;

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      <div className={styles.toolBar}>
        <span className={styles.count}>{checklists.length} checklists</span>
        {!showCreate && (
          <button className={styles.addBtn} onClick={() => setShowCreate(true)}>+ New Checklist</button>
        )}
      </div>

      {showCreate && (
        <Card className={styles.formCard}>
          <h4 className={styles.formTitle}>New Checklist</h4>
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Office *</label>
              <select className={styles.fieldInput} value={createForm.officeLocationId} onChange={e => setCreateForm(p => ({ ...p, officeLocationId: parseInt(e.target.value) }))}>
                <option value={0}>— Select —</option>
                {offices.map(o => <option key={o.officeLocationId} value={o.officeLocationId}>{o.name}</option>)}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Employment Type *</label>
              <select className={styles.fieldInput} value={createForm.employmentType} onChange={e => setCreateForm(p => ({ ...p, employmentType: e.target.value }))}>
                <option value="">— Select —</option>
                {EMPLOYMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Name *</label>
              <input className={styles.fieldInput} value={createForm.name} onChange={e => setCreateForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Dubai — Permanent Employee Onboarding" />
            </div>
          </div>
          <div className={styles.formActions}>
            <button className={styles.cancelBtn} onClick={() => setShowCreate(false)}>Cancel</button>
            <button className={styles.saveBtn} onClick={handleCreate} disabled={creating}>{creating ? 'Creating...' : 'Create'}</button>
          </div>
        </Card>
      )}

      {checklists.length === 0 && !showCreate ? (
        <Card><p style={{ color: '#9CA3AF', textAlign: 'center', padding: '30px 0' }}>No checklists configured yet.</p></Card>
      ) : (
        checklists.map(cl => (
          <div key={cl.checklistId} style={{ marginBottom: '8px' }}>
            <Card>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleExpand(cl.checklistId)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, color: '#0A2240', fontSize: '14px' }}>{cl.name}</span>
                  <span className={styles.badge}>{cl.officeName}</span>
                  <span className={styles.badge}>{cl.employmentType}</span>
                  <span style={{ fontSize: '12px', color: '#6B7280' }}>{cl.itemCount} items</span>
                  {!cl.isActive && <span style={{ fontSize: '11px', color: '#DC2626', fontWeight: 600 }}>INACTIVE</span>}
                </div>
                <span style={{ color: '#9CA3AF' }}>{expandedId === cl.checklistId ? '▲' : '▼'}</span>
              </div>

              {expandedId === cl.checklistId && (
                <div style={{ borderTop: '1px solid #E5E7EB', marginTop: '12px', paddingTop: '12px' }}>
                  {detailLoading ? <LoadingSpinner message="Loading..." /> : detail ? (
                    <>
                      {editingChecklist ? (
                        <Card className={styles.formCard}>
                          <div className={styles.formGrid}>
                            <div className={styles.field}>
                              <label className={styles.fieldLabel}>Name</label>
                              <input className={styles.fieldInput} value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} />
                            </div>
                            <div className={styles.checkField}>
                              <label><input type="checkbox" checked={editForm.isActive} onChange={e => setEditForm(p => ({ ...p, isActive: e.target.checked }))} /> Active</label>
                            </div>
                          </div>
                          <div className={styles.formActions}>
                            <button className={styles.cancelBtn} onClick={() => setEditingChecklist(false)}>Cancel</button>
                            <button className={styles.saveBtn} onClick={handleUpdateChecklist} disabled={savingChecklist}>{savingChecklist ? 'Saving...' : 'Save'}</button>
                          </div>
                        </Card>
                      ) : (
                        <div className={styles.toolBar}>
                          <span className={styles.count}>
                            {detail.items.length} items · Created by {detail.createdBy}
                          </span>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button className={styles.editBtn} onClick={() => setEditingChecklist(true)}>Edit</button>
                            <button className={styles.addBtn} onClick={startAddItem}>+ Add Item</button>
                          </div>
                        </div>
                      )}

                      {showItemForm && (
                        <Card className={styles.formCard}>
                          <h4 className={styles.formTitle}>{editingItemId ? 'Edit Item' : 'New Item'}</h4>
                          <div className={styles.formGrid}>
                            <div className={styles.field}>
                              <label className={styles.fieldLabel}>Document Name *</label>
                              <input className={styles.fieldInput} value={itemForm.documentName} onChange={e => setItemForm(p => ({ ...p, documentName: e.target.value }))} placeholder="e.g. Passport Copy" />
                            </div>
                            <div className={styles.field}>
                              <label className={styles.fieldLabel}>Category *</label>
                              <select className={styles.fieldInput} value={itemForm.documentCategory} onChange={e => setItemForm(p => ({ ...p, documentCategory: e.target.value }))}>
                                {DOC_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                              </select>
                            </div>
                            <div className={styles.field}>
                              <label className={styles.fieldLabel}>Sort Order</label>
                              <input className={styles.fieldInput} type="number" value={itemForm.sortOrder} onChange={e => setItemForm(p => ({ ...p, sortOrder: parseInt(e.target.value) || 0 }))} />
                            </div>
                            <div className={styles.field}>
                              <label className={styles.fieldLabel}>Notes</label>
                              <input className={styles.fieldInput} value={itemForm.notes} onChange={e => setItemForm(p => ({ ...p, notes: e.target.value }))} placeholder="e.g. Must be attested by UAE embassy" />
                            </div>
                            <div className={styles.checkField}>
                              <label><input type="checkbox" checked={itemForm.isRequired} onChange={e => setItemForm(p => ({ ...p, isRequired: e.target.checked }))} /> Required</label>
                            </div>
                            <div className={styles.checkField}>
                              <label><input type="checkbox" checked={itemForm.hasExpiry} onChange={e => setItemForm(p => ({ ...p, hasExpiry: e.target.checked }))} /> Has Expiry Date</label>
                            </div>
                          </div>
                          <div className={styles.formActions}>
                            <button className={styles.cancelBtn} onClick={() => { setShowItemForm(false); setEditingItemId(null); }}>Cancel</button>
                            <button className={styles.saveBtn} onClick={handleSaveItem} disabled={savingItem}>{savingItem ? 'Saving...' : editingItemId ? 'Update' : 'Add Item'}</button>
                          </div>
                        </Card>
                      )}

                      {detail.items.length === 0 ? (
                        <p style={{ color: '#9CA3AF', textAlign: 'center', padding: '20px 0', fontSize: '13px' }}>No items yet. Click "+ Add Item" to add documents.</p>
                      ) : (
                        <Card padding={false}>
                          <table className={styles.table}>
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Document</th>
                                <th>Category</th>
                                <th>Required</th>
                                <th>Expiry</th>
                                <th>Notes</th>
                                <th style={{ width: '80px' }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {detail.items.map(item => (
                                <tr key={item.checklistItemId}>
                                  <td style={{ color: '#9CA3AF' }}>{item.sortOrder}</td>
                                  <td style={{ fontWeight: 500 }}>{item.documentName}</td>
                                  <td><span className={styles.badge}>{item.documentCategory}</span></td>
                                  <td>{item.isRequired ? '✓' : '—'}</td>
                                  <td>{item.hasExpiry ? '✓' : '—'}</td>
                                  <td style={{ color: '#6B7280', fontSize: '12px' }}>{item.notes || '—'}</td>
                                  <td>
                                    <div style={{ display: 'flex', gap: '4px' }}>
                                      <button className={styles.editBtn} onClick={() => startEditItem(item)}>Edit</button>
                                      <button className={styles.deleteBtn} onClick={() => setDeleteTarget({ checklistId: detail.checklistId, itemId: item.checklistItemId, name: item.documentName })}>✕</button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Card>
                      )}
                    </>
                  ) : null}
                </div>
              )}
            </Card>
          </div>
        ))
      )}

      <ConfirmDialog open={deleteTarget !== null} title="Remove Checklist Item"
        message={`Remove "${deleteTarget?.name}"? This cannot be undone.`}
        confirmLabel="Remove" confirmVariant="danger" onConfirm={handleDeleteItem} onCancel={() => setDeleteTarget(null)} />
    </div>
  );
}
